import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDollarSign, FiTarget, FiPercent, FiMousePointer, FiRefreshCw, FiEye } = FiIcons;

const MetaAds = ({ client, data }) => {
  const safeData = data || {
    roas: 0, costPerResult: 0, convRate: 0, ctr: 0, frequency: 0, cpm: 0,
    history: [], adSets: []
  };

  const historyData = safeData.history && safeData.history.length > 0 ? safeData.history : [{ month: 'No Data', roas: 0, ctr: 0 }];

  const metrics = [
    { title: 'ROAS', value: `${safeData.roas}x`, icon: FiDollarSign, color: 'text-green-600' },
    { title: 'Cost / Result', value: `$${safeData.costPerResult}`, icon: FiTarget, color: 'text-blue-600' },
    { title: 'Conv. Rate', value: `${safeData.convRate}%`, icon: FiPercent, color: 'text-purple-600' },
    { title: 'CTR', value: `${safeData.ctr}%`, icon: FiMousePointer, color: 'text-orange-600' },
    { title: 'Frequency', value: safeData.frequency, icon: FiRefreshCw, color: 'text-indigo-600' },
    { title: 'CPM', value: `$${safeData.cpm}`, icon: FiEye, color: 'text-pink-600' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Meta Ads (Facebook & Instagram)</h2>
        <p className="text-gray-600">Social media advertising performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-10 h-10 rounded-lg bg-opacity-10 flex items-center justify-center ${metric.color.replace('text-', 'bg-')}`}>
                <SafeIcon icon={metric.icon} className={`text-lg ${metric.color}`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-1">{metric.value}</h3>
            <p className="text-sm text-gray-600">{metric.title}</p>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">ROAS & CTR Trends</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={historyData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis yAxisId="left" stroke="#6b7280" />
            <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
            <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }} />
            <Line yAxisId="left" type="monotone" dataKey="roas" stroke="#1877F2" strokeWidth={3} name="ROAS" />
            <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="#E4405F" strokeWidth={3} name="CTR" />
          </LineChart>
        </ResponsiveContainer>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Ad Set Performance</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-600">Ad Set</th>
                <th className="text-center py-3 px-4 font-medium text-gray-600">Platform</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">Spend</th>
                <th className="text-right py-3 px-4 font-medium text-gray-600">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {safeData.adSets && safeData.adSets.map((adSet, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4 font-medium text-gray-800">{adSet.name}</td>
                  <td className="py-4 px-4 text-center">{adSet.platform}</td>
                  <td className="py-4 px-4 text-right text-gray-600">${adSet.spend?.toLocaleString()}</td>
                  <td className="py-4 px-4 text-right font-medium text-gray-800">{adSet.roas}x</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default MetaAds;